import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { register } from '../utils/authUtils';
import { RegisterForm } from '../types/user';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RegisterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #202124;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Required = styled.span`
  color: #d93025;
  font-size: 14px;
  line-height: 1;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e3e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #80868b;
  }

  &:hover {
    border-color: #dadce0;
  }
`;

const Button = styled.button<{ isLoading?: boolean }>`
  padding: 14px;
  background: ${props => props.isLoading ? '#ccc' : '#1a73e8'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  margin-top: 8px;
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    background: #1557b0;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${props => props.isLoading && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      animation: loading 1.5s infinite;
    }
  `}

  @keyframes loading {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(100%);
    }
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  font-size: 0.875rem;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fdeded;
  padding: 8px 12px;
  border-radius: 6px;
  animation: ${fadeIn} 0.3s ease-out;
  
  &::before {
    content: "⚠️";
  }
`;

const PasswordStrength = styled.div<{ strength: number }>`
  height: 4px;
  background: ${props => {
    if (props.strength === 0) return '#e1e3e6';
    if (props.strength === 1) return '#f44336';
    if (props.strength === 2) return '#fb8c00';
    if (props.strength === 3) return '#fdd835';
    return '#4caf50';
  }};
  transition: all 0.3s ease;
  border-radius: 2px;
  margin-top: 8px;
`;

const PasswordRequirements = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  font-size: 0.75rem;
  color: #5f6368;
`;

const Requirement = styled.li<{ met: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.met ? '#4caf50' : '#5f6368'};
  
  &::before {
    content: ${props => props.met ? '"✓"' : '"○"'};
  }
`;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterForm>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    if (passwordStrength < 3) {
      setError('密码强度不足');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await register(formData);
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : '注册失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const passwordRequirements = [
    { text: '至少8个字符', met: formData.password.length >= 8 },
    { text: '包含大小写字母', met: /[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) },
    { text: '包含数字', met: /[0-9]/.test(formData.password) },
    { text: '包含特殊字符', met: /[^A-Za-z0-9]/.test(formData.password) },
  ];

  return (
    <RegisterContainer>
      <Form onSubmit={handleSubmit}>
        <FormField>
          <Label>
            名称
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="text"
              name="username"
              placeholder="请输入名称"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </InputGroup>
        </FormField>
        
        <FormField>
          <Label>
            邮箱
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="email"
              name="email"
              placeholder="请输入邮箱地址"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </InputGroup>
        </FormField>
        
        <FormField>
          <Label>
            密码
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="password"
              name="password"
              placeholder="请输入密码"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <PasswordStrength strength={passwordStrength} />
            <PasswordRequirements>
              {passwordRequirements.map((req, index) => (
                <Requirement key={index} met={req.met}>
                  {req.text}
                </Requirement>
              ))}
            </PasswordRequirements>
          </InputGroup>
        </FormField>
        
        <FormField>
          <Label>
            确认密码
            <Required>*</Required>
          </Label>
          <InputGroup>
            <Input
              type="password"
              name="confirmPassword"
              placeholder="请再次输入密码"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </InputGroup>
        </FormField>
        
        <Button type="submit" isLoading={isLoading} disabled={isLoading}>
          {isLoading ? '注册中...' : '创建账户'}
        </Button>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Form>
    </RegisterContainer>
  );
};

export default Register; 